import {CancelToken} from "axios";
import {ApiMethods, ApiRequestDs, ApiServiceController, IApiExecutor} from "../../../../../../@bizkeytech/base/core";
import ApiEndpoints from "../../endpoints";
import * as models from "./index.types";

/**@inheritDoc*/
export class BizFileController extends ApiServiceController {

    /**@inheritDoc*/
    public constructor(executor: IApiExecutor | undefined) {
        super(executor);
    }

    /**
     * Creates a new BizFile access token.
     *
     * @param token                 The token of the operator.
     * @param cancelToken       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public async createAccessToken(token?: string, cancelToken?: CancelToken): models.CreateBizFileAccessTokenResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.bizFile.createAccessToken,
            cancelToken: cancelToken,
            enforceAuth: false,
            showErrorToast: false,
            headers: {
                Authorization: token ?? "00000000-0000-0000-0000-000000000000",
            },
        });
    }

    /**
     * Uploads the initial file chunk to the server.
     *
     * @param request               The argument to upload the initial file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param cancelToken       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public async uploadFile(
        request: models.UploadFileRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        cancelToken?: CancelToken
    ): models.UploadFileResponse {
        const formData = new FormData();
        formData.append('firstChunk', request.file, request.fileName);
        formData.append('totalFileSize', request.file, request.fileSize.toString());
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.bizFile.uploadFile,
            data: formData,
            onUploadProgress: onUploadProgress,
            cancelToken: cancelToken,
        });
    }

    /**
     * Submits a file chunk to the server.
     *
     * @param request               The request to upload a file chunk.
     * @param onUploadProgress      The callback that will be called when the upload progress changes.
     * @param cancelToken       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public async uploadRemainingChunk(
        request: models.UploadRemainingChunkRequest,
        onUploadProgress: ApiRequestDs['onUploadProgress'],
        cancelToken?: CancelToken,
    ): models.UploadRemainingChunkResponse {
        const formData = new FormData();
        formData.append('nextChunk', request.nextChunk, request.nextChunkFileName);
        formData.append('fileId', request.fileId);
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.bizFile.uploadRemainingChunk,
            data: formData,
            onUploadProgress,
            cancelToken: cancelToken,
        })
    }

    /**
     * Creates a storage account for the partner with default capacity value.
     *
     * @param cancelToken       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public async createStorageAccount(cancelToken?: CancelToken): models.CreateStorageAccountResponse {
        return this.executor?.execute({
            method: ApiMethods.post,
            url: ApiEndpoints.bizFile.createStorageAccount,
            cancelToken: cancelToken,
            showErrorToast: false,
        });
    }

    /**
     * Gets the information of the current storage account.
     *
     * @param cancelToken       The abort controller that will be used to cancel the request.
     * @returns                     The response from the server.
     */
    public async getStorageAccount(cancelToken?: CancelToken): models.GetStorageAccountResponse {
        return this.executor?.execute({
            method: ApiMethods.get,
            url: ApiEndpoints.bizFile.getStorageAccount,
            cancelToken: cancelToken,
            showErrorToast: false,
        });
    }
}

export * from './index.types';
export default BizFileController;